import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectQueuedCards, selectQueuedCardsDisplay, selectParallelData } from '../../store/card/card.selector.js';
import { mergeQueuedCardsWithFirestore } from '../../utils/firebase/firebase.utils.js';
import { selectCardCount, selectUser } from '../../store/user/user.selector.js';
import { clearCardsFromQueue } from '../../store/card/card.action.js';

// styles
import {
    QueuedCardsContainer,
    QueueHeader,
    QueueHeaderLeft,
    QueueBody,
    QueueFooter,
    LoadingOverlay,
    LoadingContent,
    LoadingText
} from './QueuedCards.styles.js';

// components
import QueueCard from './components/queue-card/QueueCard.js';
import QueueCardCustom from './components/queue-card-custom/QueueCardCustom.js';
import LoadingIcon from '../loading-icon/LoadingIcon.js';
import MultiDropdown from '../inputs/multi-dropdown/MultiDropdown.js';

export default function QueuedCards({ isCustom = false, onAddingToCollectionChange }) {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const queuedCards = useSelector(selectQueuedCards);
    const queuedCardsDisplay = useSelector(selectQueuedCardsDisplay);
    const parallelData = useSelector(selectParallelData);
    const cardCount = useSelector(selectCardCount);
    const [isAddingToCollection, setIsAddingToCollection] = useState(false);
    const [selectedOddsProducts, setSelectedOddsProducts] = useState(['all']);

    const oddsProductOptions = useMemo(() => {
        const keys = new Set();
        parallelData.forEach(p => {
            if (p?.odds && typeof p.odds === 'object' && !Array.isArray(p.odds)) {
                Object.keys(p.odds).forEach(k => keys.add(k));
            }
        });
        return Array.from(keys).map(k => ({ value: k, label: k }));
    }, [parallelData]);

    const setAdding = (value) => {
        setIsAddingToCollection(value);
        onAddingToCollectionChange?.(value);
    };

    const handleAddToCollection = async () => {
        const userID = user.uid;
        if (userID && !isAddingToCollection) {
            setAdding(true);
            try {
                await dispatch(mergeQueuedCardsWithFirestore(userID, queuedCards, cardCount));
            } catch (error) {
                console.error('Error adding cards to collection:', error);
            } finally {
                setAdding(false);
            }
        }
    };

    const handleClearQueue = () => {
        dispatch(clearCardsFromQueue());
    }

    return (
        <>
            {isAddingToCollection && (
                <LoadingOverlay>
                    <LoadingContent>
                        <LoadingIcon />
                        <LoadingText>Cards are being added, please do not refresh the page...</LoadingText>
                    </LoadingContent>
                </LoadingOverlay>
            )}
            {queuedCards.totalCardCount !== 0 && (
                <QueuedCardsContainer $isCustom={isCustom}>
                    <QueueHeader>
                        <QueueHeaderLeft>
                            <h3>QUEUED CARDS</h3>
                            <h4>
                                {queuedCards.totalCardCount && queuedCards.totalCardCount !== 0
                                    ? queuedCards.totalCardCount
                                    : ''}
                            </h4>
                        </QueueHeaderLeft>
                        <MultiDropdown
                            label="ODDS"
                            options={oddsProductOptions}
                            selectedValues={selectedOddsProducts}
                            onChange={setSelectedOddsProducts}
                        />
                    </QueueHeader>

                    <QueueBody $isCustom={isCustom}>
                        {queuedCardsDisplay
                            .slice() // Create a shallow copy to avoid mutating the original array
                            .reverse() // Reverse the queuedCardsDisplay array
                            .map((card, cardIndex) =>
                                card.card.collectedCards
                                    .slice() // Create a shallow copy of collectedCards
                                    .reverse() // Reverse the collectedCards array
                                    .map((parallel, parallelIndex) => 
                                        isCustom ? (
                                            <QueueCardCustom
                                                key={`${cardIndex}-${parallelIndex}`}
                                                card={card}
                                                parallel={parallel}
                                                index={cardIndex}
                                                selectedOddsProducts={selectedOddsProducts}
                                                parallelData={parallelData}
                                            />
                                        ) : (
                                            <QueueCard
                                                key={`${cardIndex}-${parallelIndex}`}
                                                card={card}
                                                parallel={parallel}
                                                index={cardIndex}
                                                selectedOddsProducts={selectedOddsProducts}
                                                parallelData={parallelData}
                                            />
                                        )
                                    )
                            )}
                    </QueueBody>

                    <QueueFooter>
                        <button onClick={handleClearQueue} disabled={isAddingToCollection}>CLEAR QUEUE</button>
                        <button onClick={handleAddToCollection} disabled={isAddingToCollection}>
                            {isAddingToCollection ? (
                                <>
                                    <LoadingIcon size="small" />
                                </>
                            ) : (
                                'ADD TO COLLECTION'
                            )}
                        </button>
                    </QueueFooter>
                </QueuedCardsContainer>
            )}
        </>
    );
}